import {isMobile, isTablet, isSafari} from 'react-device-detect';
import {VideoConstraints} from './types';

const isMobileOrTablet = () => isMobile || isTablet;

type GetMobileOrDesktopVideoConstraintsProps = {
  deviceId?: string;
  facingMode?: string;
  additional?: VideoConstraints;
  optimizedForLiveness?: boolean;
};

const getLivenessOptimizedConstraints = (
  deviceId?: string,
  additional?: VideoConstraints,
) => {
  const additionalConstraints =
    typeof additional === 'object' && additional !== null ? additional : {};

  const baseConstraints = {
    deviceId: deviceId ? {exact: deviceId} : undefined,
    facingMode: {ideal: 'user'},
    width: {min: 320, ideal: 800},
    height: {min: 240, ideal: 600},
    frameRate: {ideal: 24},
    resizeMode: 'crop-and-scale' as const,
    aspectRatio: {ideal: 1.333},
    ...additionalConstraints,
  };

  if (isMobileOrTablet()) {
    return {
      ...baseConstraints,
      width: {min: 320, ideal: 640},
      height: {min: 240, ideal: 480},
      aspectRatio: {ideal: 1},
      facingMode: {ideal: 'environment'},
      ...additionalConstraints,
    };
  }

  return baseConstraints;
};

function getReducedVideoConstraints({
  deviceId,
  facingMode,
  additional,
}: GetMobileOrDesktopVideoConstraintsProps) {
  const additionalConstraints = typeof additional === 'object' ? additional : {};

  return {
    deviceId: deviceId ? {exact: deviceId} : undefined,
    facingMode: {ideal: facingMode || 'user'},
    width: {min: 320, ideal: 640},
    height: {min: 240, ideal: 480},
    ...additionalConstraints,
  };
}

function getMobileOrDesktopVideoConstraints({
  deviceId,
  facingMode,
  additional,
  optimizedForLiveness = false,
}: GetMobileOrDesktopVideoConstraintsProps) {
  const additionalConstraints = typeof additional === 'object' ? additional : {};

  if (optimizedForLiveness) {
    return getLivenessOptimizedConstraints(deviceId, additionalConstraints);
  }

  const initialConstraints = {
    deviceId: deviceId ? {exact: deviceId} : undefined,
    facingMode: {ideal: facingMode || 'user'},
    width: isSafari ? {min: 320, ideal: 1920} : {min: 320, ideal: 4096, max: 4096},
    height: isSafari ? {min: 240, ideal: 1080} : {min: 240, ideal: 2160, max: 2160},
    frameRate: {ideal: 30},
    resizeMode: 'none',
    aspectRatio: {ideal: 1.777},
    ...additionalConstraints,
  };

  if (isMobileOrTablet()) {
    return {
      ...initialConstraints,
      aspectRatio: {ideal: 1},

      facingMode: {
        ideal: 'environment',
      },
      ...additionalConstraints,
    };
  }

  // Safari on desktop often has issues with exact constraints
  if (isSafari && !isMobile) {
    return {
      deviceId: deviceId ? {ideal: deviceId} : undefined,
      width: {min: 320, ideal: 1920},
      height: {min: 240, ideal: 1080},
      ...additionalConstraints,
    };
  }

  return initialConstraints;
}

function isDOMException(error: string | DOMException | Error): error is DOMException {
  return error instanceof DOMException && Boolean(error?.name);
}

type GetPhotoCanvasProps = {
  player?: HTMLVideoElement | null;
  container?: HTMLDivElement | null;
  canvas?: HTMLCanvasElement | null;
  scanArea?: HTMLDivElement | null;
  cropToScanArea?: boolean;
};
function getPhotoCanvas({
  player,
  container,
  scanArea,
  canvas,
  cropToScanArea,
}: GetPhotoCanvasProps) {
  if (canvas) {
    const playerWidth = player?.videoWidth ?? 1280;
    const playerHeight = player?.videoHeight ?? 720;
    const playerAR = playerWidth / playerHeight;

    const canvasWidth = container?.offsetWidth ?? 1280;
    const canvasHeight = container?.offsetHeight ?? 1280;
    const canvasAR = canvasWidth / canvasHeight;

    let sX, sY, sW, sH;

    if (playerAR > canvasAR) {
      sH = playerHeight;
      sW = playerHeight * canvasAR;
      sX = (playerWidth - sW) / 2;
      sY = 0;
    } else {
      sW = playerWidth;
      sH = playerWidth / canvasAR;
      sX = 0;
      sY = (playerHeight - sH) / 2;
    }

    if (cropToScanArea && scanArea && player) {
      const scanAreaRect = scanArea.getBoundingClientRect();

      const playerClientWidth = player.clientWidth;
      const playerClientHeight = player.clientHeight;

      const scaleY = playerHeight / playerClientHeight;

      const offsetX = (playerWidth - playerClientWidth * scaleY) / 2;
      const offsetY = (playerHeight - playerClientHeight * scaleY) / 2;

      const cropX = offsetX + ((playerClientWidth - scanAreaRect.width) / 2) * scaleY;
      const cropY = offsetY + ((playerClientHeight - scanAreaRect.height) / 2) * scaleY;
      const cropWidth = scanAreaRect.width * scaleY;
      const cropHeight = scanAreaRect.height * scaleY;

      canvas.width = cropWidth;
      canvas.height = cropHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(
          player,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          cropWidth,
          cropHeight,
        );
      }
    } else {
      canvas.width = sW;
      canvas.height = sH;

      const context = canvas.getContext('2d');
      if (context && player) {
        context.drawImage(player, sX, sY, sW, sH, 0, 0, sW, sH);
      }
    }

    return canvas;
  }
}

export {
  isDOMException,
  getPhotoCanvas,
  isMobileOrTablet,
  getMobileOrDesktopVideoConstraints,
  getReducedVideoConstraints,
};
